import { shouldEvictApplicationQuery } from "@client/offline/artifactQueryPersistence";
import { revokeApplicationCache } from "@client/offline/revokeApplicationCache";
import type { RoundAggregate } from "@engine/round/aggregate";
import type { QueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { trpc, type trpcVanilla } from "@client/trpc";
import { isApplicationBuilderProposal } from "@lib/applicationProposalDisplay";
import { isTransportError } from "@client/offline/offlineCache";
import { beginIdentityScopedPrefetch } from "./funder-workspace/prefetchIdentityGuard";

/** Warm the same primary-text queries that proposal display and copy consume. */
export async function prefetchProposalApplications(
  round: RoundAggregate,
  queryClient: QueryClient,
  readApplication: typeof trpcVanilla.getProposalApplication.query,
) {
  const ids = Object.values(round.proposalForms)
    .filter((form) => form.status === "active")
    .flatMap((form) =>
      Object.values(form.proposals)
        .filter((proposal) => isApplicationBuilderProposal(proposal, form.id))
        .map((proposal) => proposal.id),
    );
  const guard = beginIdentityScopedPrefetch(queryClient);

  try {
    for (let offset = 0; offset < ids.length; offset += 8) {
      if (guard.viewerChanged()) {
        return;
      }

      const results = await Promise.allSettled(
        ids.slice(offset, offset + 8).map(async (proposalId) => {
          const input = { roundId: round.id, proposalId };
          const queryKey = guard.warm(
            getQueryKey(trpc.getProposalApplication, input, "query"),
          );

          try {
            await queryClient.fetchQuery({
              queryKey,
              queryFn: () => readApplication(input),
              staleTime: 0,
              retry: false,
            });
          } catch (error) {
            if (isTransportError(error)) {
              return;
            }

            if (shouldEvictApplicationQuery(queryKey, error)) {
              await revokeApplicationCache(queryClient, queryKey, error, true);
              return;
            }

            throw error;
          }
        }),
      );
      const failure = results.find((result) => result.status === "rejected");

      // Offline warming is supplementary: a failed source must not block round
      // loading or prevent later batches from being cached. Its query keeps the error.
      if (failure?.status === "rejected") {
        console.warn(
          "Could not warm some proposal applications",
          failure.reason,
        );
      }
    }
  } finally {
    guard.discardIfViewerChanged();
  }
}
